import { useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export interface PickupManualRepriceReasonLoan {
  obLoanId: number;
  loanNumber: string | null;
  lastPricedAt: string;
}

interface PickupManualRepriceReasonDialogProps {
  open: boolean;
  loans: PickupManualRepriceReasonLoan[];
  selectedCount: number;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string) => Promise<void> | void;
}

function formatPricedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

export function PickupManualRepriceReasonDialog({
  open,
  loans,
  selectedCount,
  submitting,
  onOpenChange,
  onConfirm
}: PickupManualRepriceReasonDialogProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  const normalizedReason = reason.trim();

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !submitting && onOpenChange(nextOpen)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Price this batch again?</DialogTitle>
          <DialogDescription>
            {loans.length} selected loan{loans.length === 1 ? ' was' : 's were'} priced within the last 60 minutes. Enter one audited reason to run the batch again.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="max-h-36 overflow-y-auto rounded-md border bg-muted/35">
            {loans.map((loan) => (
              <div
                key={loan.obLoanId}
                className="flex items-center justify-between gap-4 border-b px-3 py-2 text-xs last:border-b-0"
              >
                <span className="font-medium tabular-nums">
                  {loan.loanNumber || loan.obLoanId}
                </span>
                <span className="text-muted-foreground">
                  Priced {formatPricedAt(loan.lastPricedAt)}
                </span>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <Label htmlFor="pickup-manual-reprice-reason">Reason for pricing again</Label>
            <Textarea
              id="pickup-manual-reprice-reason"
              value={reason}
              maxLength={240}
              disabled={submitting}
              placeholder="Example: Adjudicate a suspicious same-day investor print"
              onChange={(event) => setReason(event.target.value)}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Required · 3–240 characters · saved to the audit trail</span>
              <span>{reason.length}/240</span>
            </div>
          </div>
          <p className="rounded-md border border-warning/35 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
            Analyze {selectedCount} — uses {selectedCount} OB search{selectedCount === 1 ? '' : 'es'}.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={submitting || normalizedReason.length < 3}
            onClick={() => void onConfirm(normalizedReason)}
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Search className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            {submitting ? 'Analyzing…' : `Analyze ${selectedCount}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
